import { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export type Lang = 'zh' | 'en' | 'fr' | 'es' | 'pt' | 'ja' | 'de' | 'ko' | 'ar' | 'ru';

export interface LangMeta {
  code: Lang;
  label: string;
  nativeLabel: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

export const LANG_LIST: LangMeta[] = [
  { code: 'zh', label: 'Chinese', nativeLabel: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', flag: '🇧🇷', dir: 'ltr' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語', flag: '🇯🇵', dir: 'ltr' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어', flag: '🇰🇷', dir: 'ltr' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский', flag: '🇷🇺', dir: 'ltr' },
];

// Translation map type: key = translation key, value = translated string
export type TranslationMap = Record<string, string>;

// All translations keyed by language code
const translations: Record<Lang, TranslationMap> = {
  zh: {
    // Nav
    'nav.rwaEntry': 'RWA入门',
    'nav.blockchain': '区块链基础',
    'nav.policy': '全球政策',
    'nav.cases': '87案例库',
    'nav.hkInstitutions': '香港持牌机构',
    'nav.domestic': '国内服务商',
    'nav.landing': '落地SOP',
    'nav.industry': '行业上链',
    'nav.education': '高校教育',
    'nav.news': '资讯会议',
    'nav.glossary': '名词百科',
    'nav.about': '关于我们',
    'nav.column': '清鉴纵论',
    'nav.forum': '社区论坛',
    'nav.home': '主页',
    'nav.search': '搜索案例、政策...',
    'nav.risk': '⚠ 合规',
    'nav.tagline': '2026 RWA元年 · 合规知识门户',
    // Common
    'common.readMore': '深度阅读',
    'common.viewAll': '查看全部',
    'common.learnMore': '了解更多',
    'common.subscribe': '订阅',
    'common.submit': '提交',
    'common.close': '关闭',
    'common.back': '返回',
    'common.loading': '加载中...',
    'common.noData': '暂无数据',
    'common.minutes': '分钟',
    'common.views': '次阅读',
    'common.likes': '获赞',
    'common.comments': '评论',
    'common.articles': '篇文章',
    'common.enroll': '立即报名',
    'common.courses': '课程',
    'common.cases': '案例',
    'common.experts': '专家',
    'common.policy': '政策',
    'common.compliance': '合规',
    'common.riskWarning': '风险提示',
    // Hero
    'hero.badge': '2026 RWA元年',
    'hero.title1': '看懂RWA',
    'hero.title2': '上RWA99',
    'hero.subtitle': '中国最权威的RWA真实世界资产代币化知识门户。合规第一，科普为本，本站不募资、不推介、不交易。',
    // Footer
    'footer.disclaimer': '本站内容仅供教育目的，不构成任何投资建议。RWA投资存在风险，请在专业人士指导下谨慎决策。',
    'footer.rights': '版权所有',
    // Column
    'column.title': '清鉴纵论',
    'column.subtitle': 'ChaInsight Column',
    'column.desc': '独立视角，深度剖析。以清醒之鉴，纵论RWA行业的政策、合规与未来。',
    'column.subscribe': '订阅专栏推送',
    'column.featured': '精选长文',
    'column.latest': '最新文章',
    'column.about': '关于本栏目',
    'column.stats': '专栏数据',
    'column.collections': '主题合集',
    'column.clearFilter': '清除筛选',
    'column.filterHint': '点击合集可过滤展示该主题所有文章',
    // Courses
    'courses.title': '专业课程',
    'courses.enroll': '立即报名，抢先体验',
    'courses.waitlist': '加入等待名单（免费）',
    'courses.permanent': '永久有效',
    'courses.modules': '大模块',
    'courses.lessons': '节课',
    'courses.students': '名学员',
    // Cases
    'cases.title': '全球RWA案例库',
    'cases.subtitle': '87个真实案例，覆盖债券、房地产、大宗商品、股权、碳信用等全品类',
    // BBS
    'bbs.title': 'RWA社区论坛',
    'bbs.subtitle': '与全球RWA从业者交流',
    'bbs.newPost': '发布新帖',
    'bbs.login': '登录',
    'bbs.register': '注册',
  },
  en: {
    'nav.rwaEntry': 'RWA Basics',
    'nav.blockchain': 'Blockchain',
    'nav.policy': 'Global Policy',
    'nav.cases': '87 Cases',
    'nav.hkInstitutions': 'HK Licensed',
    'nav.domestic': 'Domestic',
    'nav.landing': 'Enterprise SOP',
    'nav.industry': 'Industry Guide',
    'nav.education': 'Education',
    'nav.news': 'News & Events',
    'nav.glossary': 'Glossary',
    'nav.about': 'About',
    'nav.column': 'Column',
    'nav.forum': 'Forum',
    'nav.home': 'Home',
    'nav.search': 'Search cases, policies...',
    'nav.risk': '⚠ Risk',
    'nav.tagline': '2026 RWA Era · Compliance Portal',
    'common.readMore': 'Read More',
    'common.viewAll': 'View All',
    'common.learnMore': 'Learn More',
    'common.subscribe': 'Subscribe',
    'common.submit': 'Submit',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.loading': 'Loading...',
    'common.noData': 'No data',
    'common.minutes': 'min',
    'common.views': 'Views',
    'common.likes': 'Likes',
    'common.comments': 'Comments',
    'common.articles': 'Articles',
    'common.enroll': 'Enroll Now',
    'common.courses': 'Courses',
    'common.cases': 'Cases',
    'common.experts': 'Experts',
    'common.policy': 'Policy',
    'common.compliance': 'Compliance',
    'common.riskWarning': 'Risk Warning',
    'hero.badge': '2026 RWA Era',
    'hero.title1': 'Understand RWA',
    'hero.title2': 'Visit RWA99',
    'hero.subtitle': "China's most authoritative RWA tokenization knowledge portal. Compliance first, education-focused. We do not fundraise, recommend, or trade.",
    'footer.disclaimer': 'Content on this site is for educational purposes only and does not constitute investment advice. RWA investments carry risks; please make decisions carefully with professional guidance.',
    'footer.rights': 'All Rights Reserved',
    'column.title': 'ChaInsight Column',
    'column.subtitle': 'Independent Analysis',
    'column.desc': 'Independent perspective, deep analysis. Examining RWA policy, compliance, and the future.',
    'column.subscribe': 'Subscribe to Column',
    'column.featured': 'Featured Articles',
    'column.latest': 'Latest Articles',
    'column.about': 'About This Column',
    'column.stats': 'Column Stats',
    'column.collections': 'Collections',
    'column.clearFilter': 'Clear Filter',
    'column.filterHint': 'Click a collection to filter articles by theme',
    'courses.title': 'Professional Courses',
    'courses.enroll': 'Enroll Now',
    'courses.waitlist': 'Join Waitlist (Free)',
    'courses.permanent': 'Lifetime Access',
    'courses.modules': 'Modules',
    'courses.lessons': 'Lessons',
    'courses.students': 'Students',
    'cases.title': 'Global RWA Case Library',
    'cases.subtitle': '87 real cases covering bonds, real estate, commodities, equity, carbon credits and more',
    'bbs.title': 'RWA Community Forum',
    'bbs.subtitle': 'Connect with RWA practitioners worldwide',
    'bbs.newPost': 'New Post',
    'bbs.login': 'Login',
    'bbs.register': 'Register',
  },
  fr: {
    'nav.rwaEntry': 'Bases RWA',
    'nav.blockchain': 'Blockchain',
    'nav.policy': 'Politique Mondiale',
    'nav.cases': '87 Cas',
    'nav.hkInstitutions': 'Agréés HK',
    'nav.domestic': 'Prestataires',
    'nav.landing': 'SOP Entreprise',
    'nav.industry': 'Guide Sectoriel',
    'nav.education': 'Éducation',
    'nav.news': 'Actualités',
    'nav.glossary': 'Glossaire',
    'nav.about': 'À Propos',
    'nav.column': 'Chronique',
    'nav.forum': 'Forum',
    'nav.home': 'Accueil',
    'nav.search': 'Rechercher cas, politiques...',
    'nav.risk': '⚠ Risque',
    'nav.tagline': '2026 Ère RWA · Portail Conformité',
    'common.readMore': 'Lire Plus',
    'common.viewAll': 'Voir Tout',
    'common.learnMore': 'En Savoir Plus',
    'common.subscribe': "S'abonner",
    'common.submit': 'Soumettre',
    'common.close': 'Fermer',
    'common.back': 'Retour',
    'common.loading': 'Chargement...',
    'common.noData': 'Aucune donnée',
    'common.minutes': 'min',
    'common.views': 'Vues',
    'common.likes': "J'aime",
    'common.comments': 'Commentaires',
    'common.articles': 'Articles',
    'common.enroll': "S'inscrire",
    'common.courses': 'Cours',
    'common.cases': 'Cas',
    'common.experts': 'Experts',
    'common.policy': 'Politique',
    'common.compliance': 'Conformité',
    'common.riskWarning': 'Avertissement de Risque',
    'hero.badge': 'Ère RWA 2026',
    'hero.title1': 'Comprendre les RWA',
    'hero.title2': 'Visitez RWA99',
    'hero.subtitle': "Le portail de référence sur la tokenisation des actifs réels (RWA). Conformité d'abord, éducation avant tout. Nous ne levons pas de fonds, ne recommandons pas et ne négocions pas.",
    'footer.disclaimer': "Le contenu de ce site est uniquement à des fins éducatives et ne constitue pas un conseil en investissement. Les investissements RWA comportent des risques ; veuillez prendre des décisions avec l'aide de professionnels.",
    'footer.rights': 'Tous Droits Réservés',
    'column.title': 'Chronique ChaInsight',
    'column.subtitle': 'Analyse Indépendante',
    'column.desc': 'Perspective indépendante, analyse approfondie. Examen des politiques RWA, de la conformité et de l\'avenir.',
    'column.subscribe': "S'abonner à la Chronique",
    'column.featured': 'Articles Sélectionnés',
    'column.latest': 'Derniers Articles',
    'column.about': 'À Propos de Cette Chronique',
    'column.stats': 'Statistiques',
    'column.collections': 'Collections',
    'column.clearFilter': 'Effacer le Filtre',
    'column.filterHint': 'Cliquez sur une collection pour filtrer les articles par thème',
    'courses.title': 'Cours Professionnels',
    'courses.enroll': "S'inscrire Maintenant",
    'courses.waitlist': "Rejoindre la Liste d'Attente (Gratuit)",
    'courses.permanent': 'Accès à Vie',
    'courses.modules': 'Modules',
    'courses.lessons': 'Leçons',
    'courses.students': 'Étudiants',
    'cases.title': 'Bibliothèque de Cas RWA Mondiaux',
    'cases.subtitle': '87 cas réels couvrant obligations, immobilier, matières premières, actions, crédits carbone et plus',
    'bbs.title': 'Forum Communautaire RWA',
    'bbs.subtitle': 'Connectez-vous avec des praticiens RWA du monde entier',
    'bbs.newPost': 'Nouveau Post',
    'bbs.login': 'Connexion',
    'bbs.register': 'Inscription',
  },
  es: {
    'nav.rwaEntry': 'Bases RWA',
    'nav.blockchain': 'Blockchain',
    'nav.policy': 'Política Global',
    'nav.cases': '87 Casos',
    'nav.hkInstitutions': 'Licenciados HK',
    'nav.domestic': 'Proveedores',
    'nav.landing': 'SOP Empresarial',
    'nav.industry': 'Guía Sectorial',
    'nav.education': 'Educación',
    'nav.news': 'Noticias',
    'nav.glossary': 'Glosario',
    'nav.about': 'Acerca de',
    'nav.column': 'Columna',
    'nav.forum': 'Foro',
    'nav.home': 'Inicio',
    'nav.search': 'Buscar casos, políticas...',
    'nav.risk': '⚠ Riesgo',
    'nav.tagline': '2026 Era RWA · Portal de Cumplimiento',
    'common.readMore': 'Leer Más',
    'common.viewAll': 'Ver Todo',
    'common.learnMore': 'Saber Más',
    'common.subscribe': 'Suscribirse',
    'common.submit': 'Enviar',
    'common.close': 'Cerrar',
    'common.back': 'Volver',
    'common.loading': 'Cargando...',
    'common.noData': 'Sin datos',
    'common.minutes': 'min',
    'common.views': 'Vistas',
    'common.likes': 'Me gusta',
    'common.comments': 'Comentarios',
    'common.articles': 'Artículos',
    'common.enroll': 'Inscribirse',
    'common.courses': 'Cursos',
    'common.cases': 'Casos',
    'common.experts': 'Expertos',
    'common.policy': 'Política',
    'common.compliance': 'Cumplimiento',
    'common.riskWarning': 'Advertencia de Riesgo',
    'hero.badge': 'Era RWA 2026',
    'hero.title1': 'Entiende los RWA',
    'hero.title2': 'Visita RWA99',
    'hero.subtitle': 'El portal de referencia sobre tokenización de activos del mundo real (RWA). Cumplimiento primero, educación ante todo. No recaudamos fonds, no recomendamos ni negociamos.',
    'footer.disclaimer': 'El contenido de este sitio es solo para fines educativos y no constituye asesoramiento de inversión. Las inversiones en RWA conllevan riesgos; tome decisiones con orientación profesional.',
    'footer.rights': 'Todos los Derechos Reservados',
    'column.title': 'Columna ChaInsight',
    'column.subtitle': 'Análisis Independiente',
    'column.desc': 'Perspectiva independiente, análisis profundo. Examinando políticas RWA, cumplimiento y el futuro.',
    'column.subscribe': 'Suscribirse a la Columna',
    'column.featured': 'Artículos Destacados',
    'column.latest': 'Últimos Artículos',
    'column.about': 'Acerca de Esta Columna',
    'column.stats': 'Estadísticas',
    'column.collections': 'Colecciones',
    'column.clearFilter': 'Limpiar Filtro',
    'column.filterHint': 'Haz clic en una colección para filtrar artículos por tema',
    'courses.title': 'Cursos Profesionales',
    'courses.enroll': 'Inscribirse Ahora',
    'courses.waitlist': 'Unirse a la Lista de Espera (Gratis)',
    'courses.permanent': 'Acceso de por Vida',
    'courses.modules': 'Módulos',
    'courses.lessons': 'Lecciones',
    'courses.students': 'Estudiantes',
    'cases.title': 'Biblioteca de Casos RWA Globales',
    'cases.subtitle': '87 casos reales que cubren bonos, bienes raíces, materias primas, acciones, créditos de carbono y más',
    'bbs.title': 'Foro Comunitario RWA',
    'bbs.subtitle': 'Conéctate con profesionales RWA de todo el mundo',
    'bbs.newPost': 'Nueva Publicación',
    'bbs.login': 'Iniciar Sesión',
    'bbs.register': 'Registrarse',
  },
  pt: {
    'nav.rwaEntry': 'Bases RWA',
    'nav.blockchain': 'Blockchain',
    'nav.policy': 'Política Global',
    'nav.cases': '87 Casos',
    'nav.hkInstitutions': 'Licenciados HK',
    'nav.domestic': 'Fornecedores',
    'nav.landing': 'SOP Empresarial',
    'nav.industry': 'Guia Setorial',
    'nav.education': 'Educação',
    'nav.news': 'Notícias',
    'nav.glossary': 'Glossário',
    'nav.about': 'Sobre',
    'nav.column': 'Coluna',
    'nav.forum': 'Fórum',
    'nav.home': 'Início',
    'nav.search': 'Pesquisar casos, políticas...',
    'nav.risk': '⚠ Risco',
    'nav.tagline': '2026 Era RWA · Portal de Conformidade',
    'common.readMore': 'Ler Mais',
    'common.viewAll': 'Ver Tudo',
    'common.learnMore': 'Saiba Mais',
    'common.subscribe': 'Assinar',
    'common.submit': 'Enviar',
    'common.close': 'Fechar',
    'common.back': 'Voltar',
    'common.loading': 'Carregando...',
    'common.noData': 'Sem dados',
    'common.minutes': 'min',
    'common.views': 'Visualizações',
    'common.likes': 'Curtidas',
    'common.comments': 'Comentários',
    'common.articles': 'Artigos',
    'common.enroll': 'Inscrever-se',
    'common.courses': 'Cursos',
    'common.cases': 'Casos',
    'common.experts': 'Especialistas',
    'common.policy': 'Política',
    'common.compliance': 'Conformidade',
    'common.riskWarning': 'Aviso de Risco',
    'hero.badge': 'Era RWA 2026',
    'hero.title1': 'Entenda os RWA',
    'hero.title2': 'Visite RWA99',
    'hero.subtitle': 'O portal de referência sobre tokenização de ativos do mundo real (RWA). Conformidade em primeiro lugar, educação acima de tudo. Não recaudamos recursos, não recomendamos nem negociamos.',
    'footer.disclaimer': 'O conteúdo deste site é apenas para fins educacionais e não constitui aconselhamento de investimento. Os investimentos em RWA envolvem riscos; tome decisões com orientação profissional.',
    'footer.rights': 'Todos os Direitos Reservados',
    'column.title': 'Coluna ChaInsight',
    'column.subtitle': 'Análise Independente',
    'column.desc': 'Perspectiva independente, análise profunda. Examinando políticas RWA, conformidade e o futuro.',
    'column.subscribe': 'Assinar a Coluna',
    'column.featured': 'Artigos em Destaque',
    'column.latest': 'Últimos Artigos',
    'column.about': 'Sobre Esta Coluna',
    'column.stats': 'Estatísticas',
    'column.collections': 'Coleções',
    'column.clearFilter': 'Limpar Filtro',
    'column.filterHint': 'Clique em uma coleção para filtrar artigos por tema',
    'courses.title': 'Cursos Profissionais',
    'courses.enroll': 'Inscrever-se Agora',
    'courses.waitlist': 'Entrar na Lista de Espera (Grátis)',
    'courses.permanent': 'Acesso Vitalício',
    'courses.modules': 'Módulos',
    'courses.lessons': 'Aulas',
    'courses.students': 'Alunos',
    'cases.title': 'Biblioteca de Casos RWA Globais',
    'cases.subtitle': '87 casos reais cobrindo títulos, imóveis, commodities, ações, créditos de carbono e mais',
    'bbs.title': 'Fórum da Comunidade RWA',
    'bbs.subtitle': 'Conécte-se com profissionais RWA de todo o mundo',
    'bbs.newPost': 'Nova Publicação',
    'bbs.login': 'Entrar',
    'bbs.register': 'Registrar',
  },
  ja: {
    'nav.rwaEntry': 'RWA基礎',
    'nav.blockchain': 'ブロックチェーン',
    'nav.policy': 'グローバル政策',
    'nav.cases': '87事例',
    'nav.hkInstitutions': 'HK認可機関',
    'nav.domestic': '国内業者',
    'nav.landing': '企業SOP',
    'nav.industry': '業界ガイド',
    'nav.education': '教育',
    'nav.news': 'ニュース',
    'nav.glossary': '用語集',
    'nav.about': '概要',
    'nav.column': 'コラム',
    'nav.forum': 'フォーラム',
    'nav.home': 'ホーム',
    'nav.search': 'ケース、政策を検索...',
    'nav.risk': '⚠ リスク',
    'nav.tagline': '2026 RWA元年 · コンプライアンスポータル',
    'common.readMore': '続きを読む',
    'common.viewAll': 'すべて見る',
    'common.learnMore': '詳細を見る',
    'common.subscribe': '購読',
    'common.submit': '送信',
    'common.close': '閉じる',
    'common.back': '戻る',
    'common.loading': '読み込み中...',
    'common.noData': 'データなし',
    'common.minutes': '分',
    'common.views': '閲覧',
    'common.likes': 'いいね',
    'common.comments': 'コメント',
    'common.articles': '記事',
    'common.enroll': '今すぐ登録',
    'common.courses': 'コース',
    'common.cases': '事例',
    'common.experts': '専門家',
    'common.policy': '政策',
    'common.compliance': 'コンプライアンス',
    'common.riskWarning': 'リスク警告',
    'hero.badge': '2026 RWA元年',
    'hero.title1': 'RWAを理解する',
    'hero.title2': 'RWA99へ',
    'hero.subtitle': 'リアルワールドアセット（RWA）トークン化の権威ある知識ポータル。コンプライアンス最優先、教育重視。資金調達・推奨・取引は一切行いません。',
    'footer.disclaimer': 'このサイトのコンテンツは教育目的のみであり、投資アドバイスを構成するものではありません。RWA投資にはリスクが伴います。専門家の指導のもとで慎重に判断してください。',
    'footer.rights': '全著作権所有',
    'column.title': 'Coluna ChaInsight',
    'column.subtitle': '独立分析',
    'column.desc': '独立した視点、深い分析。RWA政策、コンプライアンス、未来を考察します。',
    'column.subscribe': 'コラムを購読',
    'column.featured': '注目記事',
    'column.latest': '最新記事',
    'column.about': 'このコラムについて',
    'column.stats': '統計',
    'column.collections': 'コレクション',
    'column.clearFilter': 'フィルターをクリア',
    'column.filterHint': 'コレクションをクリックしてテーマ別に記事をフィルタリング',
    'courses.title': 'プロフェッショナルコース',
    'courses.enroll': '今すぐ登録',
    'courses.waitlist': 'ウェイトリストに参加（無料）',
    'courses.permanent': '永久アクセス',
    'courses.modules': 'モジュール',
    'courses.lessons': 'レッスン',
    'courses.students': '受講生',
    'cases.title': 'グローバルRWA事例ライブラリ',
    'cases.subtitle': '87個の実際の事例で、債券、不動産、商品、株式、カーボンクレジットなどをカバーする',
    'bbs.title': 'RWAコミュニティフォーラム',
    'bbs.subtitle': '世界中のRWA実務家とつながる',
    'bbs.newPost': '新規投稿',
    'bbs.login': 'ログイン',
    'bbs.register': '登録',
  },
  de: {
    'nav.rwaEntry': 'RWA Grundlagen',
    'nav.blockchain': 'Blockchain',
    'nav.policy': 'Globale Politik',
    'nav.cases': '87 Fälle',
    'nav.hkInstitutions': 'HK Lizenziert',
    'nav.domestic': 'Anbieter',
    'nav.landing': 'Unternehmens-SOP',
    'nav.industry': 'Branchenführer',
    'nav.education': 'Bildung',
    'nav.news': 'Nachrichten',
    'nav.glossary': 'Glossar',
    'nav.about': 'Über uns',
    'nav.column': 'Kolumne',
    'nav.forum': 'Forum',
    'nav.home': 'Startseite',
    'nav.search': 'Fälle, Richtlinien suchen...',
    'nav.risk': '⚠ Risiko',
    'nav.tagline': '2026 RWA-Ära · Compliance-Portal',
    'common.readMore': 'Mehr Lesen',
    'common.viewAll': 'Alle Anzeigen',
    'common.learnMore': 'Mehr Erfahren',
    'common.subscribe': 'Abonnieren',
    'common.submit': 'Einreichen',
    'common.close': 'Schließen',
    'common.back': 'Zurück',
    'common.loading': 'Laden...',
    'common.noData': 'Keine Daten',
    'common.minutes': 'Min',
    'common.views': 'Aufrufe',
    'common.likes': 'Gefällt mir',
    'common.comments': 'Kommentare',
    'common.articles': 'Artikel',
    'common.enroll': 'Jetzt Anmelden',
    'common.courses': 'Kurse',
    'common.cases': 'Fälle',
    'common.experts': 'Experten',
    'common.policy': 'Politik',
    'common.compliance': 'Compliance',
    'common.riskWarning': 'Risikowarnung',
    'hero.badge': 'RWA-Ära 2026',
    'hero.title1': 'RWA Verstehen',
    'hero.title2': 'RWA99 Besuchen',
    'hero.subtitle': 'Das maßgebliche Wissensportal zur Tokenisierung realer Vermögenswerte (RWA). Compliance zuerst, Bildung im Vordergrund. Wir sammeln keine Gelder, empfehlen nichts und handeln nicht.',
    'footer.disclaimer': 'Die Inhalte dieser Website dienen nur zu Bildungszwecken und stellen keine Anlageberatung dar. RWA-Investitionen sind mit Risiken verbunden; bitte treffen Sie Entscheidungen mit professioneller Beratung.',
    'footer.rights': 'Alle Rechte Vorbehalten',
    'column.title': 'ChaInsight Kolumne',
    'column.subtitle': 'Unabhängige Analyse',
    'column.desc': 'Unabhängige Perspektive, tiefgehende Analyse. Untersuchung von RWA-Richtlinien, Compliance und Zukunft.',
    'column.subscribe': 'Kolumne Abonnieren',
    'column.featured': 'Ausgewählte Artikel',
    'column.latest': 'Neueste Artikel',
    'column.about': 'Über Diese Kolumne',
    'column.stats': 'Statistiken',
    'column.collections': 'Sammlungen',
    'column.clearFilter': 'Filter Löschen',
    'column.filterHint': 'Klicken Sie auf eine Sammlung, um Artikel nach Thema zu filtern',
    'courses.title': 'Professionelle Kurse',
    'courses.enroll': 'Jetzt Anmelden',
    'courses.waitlist': 'Warteliste Beitreten (Kostenlos)',
    'courses.permanent': 'Lebenslanger Zugang',
    'courses.modules': 'Module',
    'courses.lessons': 'Lektionen',
    'courses.students': 'Studenten',
    'cases.title': 'Globale RWA-Fallbibliothek',
    'cases.subtitle': '87 reale Fälle zu Anleihen, Immobilien, Rohstoffen, Aktien, CO2-Zertifikaten und mehr',
    'bbs.title': 'RWA-Community-Forum',
    'bbs.subtitle': 'Vernetzen Sie sich mit RWA-Praktikern weltweit',
    'bbs.newPost': 'Neuer Beitrag',
    'bbs.login': 'Anmelden',
    'bbs.register': 'Registrieren',
  },
  ko: {
    'nav.rwaEntry': 'RWA 기초',
    'nav.blockchain': '블록체인',
    'nav.policy': '글로벌 정책',
    'nav.cases': '87 사례',
    'nav.hkInstitutions': 'HK 인가기관',
    'nav.domestic': '국내 업체',
    'nav.landing': '기업 SOP',
    'nav.industry': '산업 가이드',
    'nav.education': '교육',
    'nav.news': '뉴스',
    'nav.glossary': '용어집',
    'nav.about': '소개',
    'nav.column': '칼럼',
    'nav.forum': '포럼',
    'nav.home': '홈',
    'nav.search': '사례, 정책 검색...',
    'nav.risk': '⚠ 위험',
    'nav.tagline': '2026 RWA 원년 · 컴플라이언스 포털',
    'common.readMore': '더 읽기',
    'common.viewAll': '전체 보기',
    'common.learnMore': '자세히 알아보기',
    'common.subscribe': '구독',
    'common.submit': '제출',
    'common.close': '닫기',
    'common.back': '뒤로',
    'common.loading': '로딩 중...',
    'common.noData': '데이터 없음',
    'common.minutes': '분',
    'common.views': '조회',
    'common.likes': '좋아요',
    'common.comments': '댓글',
    'common.articles': '기사',
    'common.enroll': '지금 등록',
    'common.courses': '강좌',
    'common.cases': '사례',
    'common.experts': '전문가',
    'common.policy': '정책',
    'common.compliance': '컴플라이언스',
    'common.riskWarning': '위험 경고',
    'hero.badge': '2026 RWA 원년',
    'hero.title1': 'RWA 이해하기',
    'hero.title2': 'RWA99 방문',
    'hero.subtitle': '실물 자산 토큰화(RWA)의 권위 있는 지식 포털. 컴플라이언스 우선, 교육 중심. 자금 모집, 추천, 거래는 일절 하지 않습니다.',
    'footer.disclaimer': '이 사이트의 콘텐츠는 교육 목적으로만 제공되며 투자 조언을 구성하지 않습니다. RWA 투자에는 위험이 따릅니다. 전문가의 지도 하에 신중하게 결정하십시오.',
    'footer.rights': '모든 권리 보유',
    'column.title': 'ChaInsight 칼럼',
    'column.subtitle': '독립 분석',
    'column.desc': '독립적인 시각, 심층 분석. RWA 정책, 컴플라이언스, 미래를 탐구합니다.',
    'column.subscribe': '칼럼 구독',
    'column.featured': '추천 기사',
    'column.latest': '최신 기사',
    'column.about': '이 칼럼에 대하여',
    'column.stats': '통계',
    'column.collections': '컬렉션',
    'column.clearFilter': '필터 지우기',
    'column.filterHint': '컬렉션을 클릭하여 주제별로 기사 필터링',
    'courses.title': '전문 강좌',
    'courses.enroll': '지금 등록',
    'courses.waitlist': '대기자 명단 참여 (무료)',
    'courses.permanent': '평생 이용',
    'courses.modules': '모듈',
    'courses.lessons': '강의',
    'courses.students': '수강생',
    'cases.title': '글로벌 RWA 사례 라이브러리',
    'cases.subtitle': '채권, 부동산, 원자재, 주식, 탄소 크레딧 등을 포함한 87개의 실제 사례',
    'bbs.title': 'RWA 커뮤니티 포럼',
    'bbs.subtitle': '전 세계 RWA 실무자와 연결',
    'bbs.newPost': '새 게시물',
    'bbs.login': '로그인',
    'bbs.register': '회원가입',
  },
  ar: {
    'nav.rwaEntry': 'أساسيات RWA',
    'nav.blockchain': 'بلوكتشين',
    'nav.policy': 'السياسة العالمية',
    'nav.cases': '87 حالة',
    'nav.hkInstitutions': 'مرخصو HK',
    'nav.domestic': 'مزودون',
    'nav.landing': 'SOP المؤسسي',
    'nav.industry': 'دليل القطاع',
    'nav.education': 'تعليم',
    'nav.news': 'أخبار',
    'nav.glossary': 'مسرد',
    'nav.about': 'حول',
    'nav.column': 'عمود',
    'nav.forum': 'منتدى',
    'nav.home': 'الرئيسية',
    'nav.search': 'البحث في الحالات والسياسات...',
    'nav.risk': '⚠ مخاطر',
    'nav.tagline': '2026 عصر RWA · بوابة الامتثال',
    'common.readMore': 'اقرأ المزيد',
    'common.viewAll': 'عرض الكل',
    'common.learnMore': 'اعرف المزيد',
    'common.subscribe': 'اشترك',
    'common.submit': 'إرسال',
    'common.close': 'إغلاق',
    'common.back': 'رجوع',
    'common.loading': 'جار التحميل...',
    'common.noData': 'لا توجد بيانات',
    'common.minutes': 'دقيقة',
    'common.views': 'مشاهدات',
    'common.likes': 'إعجابات',
    'common.comments': 'تعليقات',
    'common.articles': 'مقالات',
    'common.enroll': 'سجل الآن',
    'common.courses': 'دورات',
    'common.cases': 'حالات',
    'common.experts': 'خبراء',
    'common.policy': 'سياسة',
    'common.compliance': 'امتثال',
    'common.riskWarning': 'تحذير المخاطر',
    'hero.badge': 'عصر RWA 2026',
    'hero.title1': 'افهم RWA',
    'hero.title2': 'زر RWA99',
    'hero.subtitle': 'البوابة المرجعية لترميز الأصول الحقيقية (RWA). الامتثال أولاً، التعليم فوق كل شيء. لا نجمع أموالاً ولا نوصي ولا نتداول.',
    'footer.disclaimer': 'محتوى هذا الموقع لأغراض تعليمية فقط ولا يشكل نصيحة استثمارية. تنطوي استثمارات RWA على مخاطر؛ يرجى اتخاذ القرارات بتوجيه متخصص.',
    'footer.rights': 'جميع الحقوق محفوظة',
    'column.title': 'عمود ChaInsight',
    'column.subtitle': 'تحليل مستقل',
    'column.desc': 'منظور مستقل، تحليل عميق. فحص سياسات RWA والامتثال والمستقبل.',
    'column.subscribe': 'اشترك في العمود',
    'column.featured': 'مقالات مميزة',
    'column.latest': 'أحدث المقالات',
    'column.about': 'حول هذا العمود',
    'column.stats': 'إحصائيات',
    'column.collections': 'مجموعات',
    'column.clearFilter': 'مسح الفلتر',
    'column.filterHint': 'انقر على مجموعة لتصفية المقالات حسب الموضوع',
    'courses.title': 'دورات احترافية',
    'courses.enroll': 'سجل الآن',
    'courses.waitlist': 'انضم لقائمة الانتظار (مجاناً)',
    'courses.permanent': 'وصول مدى الحياة',
    'courses.modules': 'وحدات',
    'courses.lessons': 'دروس',
    'courses.students': 'طلاب',
    'cases.title': 'مكتبة حالات RWA العالمية',
    'cases.subtitle': '87 حالة حقيقية تغطي السندات والعقارات والسلع والأسهم وائتمانات الكربون والمزيد',
    'bbs.title': 'منتدى مجتمع RWA',
    'bbs.subtitle': 'تواصل مع ممارسي RWA حول العالم',
    'bbs.newPost': 'منشور جديد',
    'bbs.login': 'تسجيل الدخول',
    'bbs.register': 'التسجيل',
  },
  ru: {
    'nav.rwaEntry': 'Основы RWA',
    'nav.blockchain': 'Блокчейн',
    'nav.policy': 'Глобальная Политика',
    'nav.cases': '87 Случаев',
    'nav.hkInstitutions': 'Лицензиаты HK',
    'nav.domestic': 'Провайдеры',
    'nav.landing': 'Корпоративный SOP',
    'nav.industry': 'Отраслевой Гид',
    'nav.education': 'Образование',
    'nav.news': 'Новости',
    'nav.glossary': 'Глоссарий',
    'nav.about': 'О нас',
    'nav.column': 'Колонка',
    'nav.forum': 'Форум',
    'nav.home': 'Главная',
    'nav.search': 'Поиск случаев, политик...',
    'nav.risk': '⚠ Риск',
    'nav.tagline': '2026 Эра RWA · Портал Соответствия',
    'common.readMore': 'Читать Далее',
    'common.viewAll': 'Посмотреть Всё',
    'common.learnMore': 'Узнать Больше',
    'common.subscribe': 'Подписаться',
    'common.submit': 'Отправить',
    'common.close': 'Закрыть',
    'common.back': 'Назад',
    'common.loading': 'Загрузка...',
    'common.noData': 'Нет данных',
    'common.minutes': 'мин',
    'common.views': 'Просмотры',
    'common.likes': 'Лайки',
    'common.comments': 'Комментарии',
    'common.articles': 'Статьи',
    'common.enroll': 'Записаться',
    'common.courses': 'Курсы',
    'common.cases': 'Случаи',
    'common.experts': 'Эксперты',
    'common.policy': 'Политика',
    'common.compliance': 'Соответствие',
    'common.riskWarning': 'Предупреждение о Рисках',
    'hero.badge': 'Эра RWA 2026',
    'hero.title1': 'Понять RWA',
    'hero.title2': 'Посетить RWA99',
    'hero.subtitle': 'Авторитетный портал знаний о токенизации реальных активов (RWA). Соответствие прежде всего, образование в приоритете. Мы не привлекаем средства, не рекомендуем и не торгуем.',
    'footer.disclaimer': 'Содержимое этого сайта предназначено только для образовательных целей и не является инвестиционным советом. Инвестиции в RWA сопряжены с рисками; принимайте решения с профессиональным руководством.',
    'footer.rights': 'Все Права Защищены',
    'column.title': 'Колонка ChaInsight',
    'column.subtitle': 'Независимый Анализ',
    'column.desc': 'Независимая перспектива, глубокий анализ. Изучение политик RWA, соответствия и будущего.',
    'column.subscribe': 'Подписаться на Колонку',
    'column.featured': 'Избранные Статьи',
    'column.latest': 'Последние Статьи',
    'column.about': 'Об Этой Колонке',
    'column.stats': 'Статистика',
    'column.collections': 'Коллекции',
    'column.clearFilter': 'Очистить Фильтр',
    'column.filterHint': 'Нажмите на коллекцию для фильтрации статей по теме',
    'courses.title': 'Профессиональные Курсы',
    'courses.enroll': 'Записаться Сейчас',
    'courses.waitlist': 'Присоединиться к Списку Ожидания (Бесплатно)',
    'courses.permanent': 'Пожизненный Доступ',
    'courses.modules': 'Модули',
    'courses.lessons': 'Уроки',
    'courses.students': 'Студентов',
    'cases.title': 'Глобальная Библиотека Случаев RWA',
    'cases.subtitle': '87 реальных случаев, охватывающих облигации, недвижимость, сырьё, акции, углеродные кредиты и многое другое',
    'bbs.title': 'Форум Сообщества RWA',
    'bbs.subtitle': 'Общайтесь с практиками RWA по всему миру',
    'bbs.newPost': 'Новый Пост',
    'bbs.login': 'Войти',
    'bbs.register': 'Зарегистрироваться',
  },
};

interface LangContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (keyOrZh: string, enOrFallback?: string) => string;
  tLegacy: (zh: string, en: string) => string;
  isRTL: boolean;
  langMeta: LangMeta;
}

const LangContext = createContext<LangContextType>({
  lang: 'zh',
  setLang: () => {},
  t: (key, fallback) => fallback ?? key,
  tLegacy: (zh) => zh,
  isRTL: false,
  langMeta: LANG_LIST[0],
});

const STORAGE_KEY = 'rwa99_lang';

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
      if (saved && LANG_LIST.find(l => l.code === saved)) return saved;
    } catch (e) { /* ignore */ }
    return 'zh';
  });

  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    try { localStorage.setItem(STORAGE_KEY, newLang); } catch (e) { /* ignore */ }
  };

  const langMeta = LANG_LIST.find(l => l.code === lang) ?? LANG_LIST[0];
  const isRTL = langMeta.dir === 'rtl';

  // Apply RTL direction to document + load Arabic font on demand
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang;

    // Dynamically load Arabic font only when needed
    if (isRTL) {
      const existingLink = document.getElementById('arabic-font-link');
      if (!existingLink) {
        const link = document.createElement('link');
        link.id = 'arabic-font-link';
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;600;700;900&display=swap';
        document.head.appendChild(link);
      }
    }
  }, [lang, isRTL]);

  const t = (keyOrZh: string, enOrFallback?: string): string => {
    // If second argument looks like a translation key (no spaces, dot notation) or is undefined,
    // treat first arg as a translation key
    // If second argument is a natural language string (has spaces or is a sentence), treat as legacy t(zh, en)
    const isLegacyCall = enOrFallback !== undefined && (
      enOrFallback.includes(' ') || 
      enOrFallback.length > 30 ||
      /[A-Z]/.test(enOrFallback.charAt(0))
    );

    if (isLegacyCall) {
      // Legacy mode: t(zh, en) - for backward compatibility
      if (lang === 'zh') return keyOrZh;
      if (lang === 'en') return enOrFallback!;
      // For other languages, return English as base
      return enOrFallback!;
    }

    // New key-based mode: t('nav.key')
    const map = translations[lang];
    if (map && map[keyOrZh]) return map[keyOrZh];
    // Fallback to English
    const enMap = translations['en'];
    if (enMap && enMap[keyOrZh]) return enMap[keyOrZh];
    return enOrFallback ?? keyOrZh;
  };

  // Legacy support for old t(zh, en) pattern
  const tLegacy = (zh: string, en: string): string => {
    if (lang === 'zh') return zh;
    if (lang === 'en') return en;
    // For other languages, return English as base
    return en;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t, tLegacy, isRTL, langMeta }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  return useContext(LangContext);
}
